import { AnyAction } from 'redux'

import { DEFAULT_TENANT } from '~/app/constants'
import { createSelector } from 'reselect'
import { CONTENT_UPDATE, PAGE_UPDATE } from '~/app/store/constants'

export const INITIAL_STATE: State.ConfigState = {
  locale: '' as i18n.Locale,
  name: '',
  galleryId: -1,
  h1Title: '',
  market: '',
  mpvId: '',
  productKey: '',
  productVersion: 0,
  renderProperties: {} as State.RenderPropsState,
  selectedOptions: {},
  tenant: DEFAULT_TENANT,
  quantity: 100,
  quantityData: {} as Gallery.Models.Url.Quantity.Data,
  url: new URL('https://localhost:3000'),
  metadata: {} as State.ModifiedGalleryConfigMetadata,
  viewId: '',
  designEngagementViewIds: [],
  lastLoadMoreLength: 0,
  experiments: [],
  igpType: 'Standard',
  designServicesAvailable: false,
  uploadTileAvailable: true,
  dpcId: '',
  designConceptId: '',
  categoryFilters: [],
  galleryType: 'Standard',
}

export function reducer(state: State.ConfigState = INITIAL_STATE, action: AnyAction): State.ConfigState {
  switch (action.type) {
    case CONTENT_UPDATE:
      return {
        ...state,
        viewId: action.payload.config.viewId,
        designEngagementViewIds: [...state.designEngagementViewIds, action.payload.config.viewId],
        lastLoadMoreLength: action.payload.config.lastLoadMoreLength,
      }
    case PAGE_UPDATE:
      return {
        ...state,
        designEngagementViewIds: [],
      }
    default:
      return state
  }
}

export const booleanRenderPropertySelector = createSelector(
  (state: State.GlobalState) => state.config.renderProperties,
  (renderProperties) =>
    (renderPropertyName: RenderProperty): boolean =>
      renderProperties[renderPropertyName]?.toLowerCase() === 'true',
)

export const getLocaleSelector = (state: State.GlobalState): i18n.Locale => state.config.locale

export const getGalleryNameSelector = (state: State.GlobalState): string => state.config.name

export const getGalleryIdSelector = (state: State.GlobalState): number => state.config.galleryId

export const getH1Title = (state: State.GlobalState): string => state.config.h1Title

export const getMarket = (state: State.GlobalState): string => state.config.market

export const getMpvid = (state: State.GlobalState): string => state.config.mpvId

export const getProductKey = (state: State.GlobalState): string => state.config.productKey

export const getProductVersion = (state: State.GlobalState): number => state.config.productVersion

export const getMetadata = (state: State.GlobalState): State.ModifiedGalleryConfigMetadata | undefined => state.config.metadata

export const getBreadcrumbs = (state: State.GlobalState): Gallery.ConfigApi.Breadcrumb[] | undefined => state.config.metadata?.breadcrumbs

export const getProductOptionsDescriptions = (state: State.GlobalState): Gallery.ConfigApi.ProductOptionsDescription[] | undefined =>
  state.config.metadata?.productOptionsDescriptions

export const getQuicklinks = (state: State.GlobalState): Gallery.ConfigApi.QuickLink[] | undefined => state.config.metadata?.quicklinks

export const getIGPL0Url = (state: State.GlobalState): string | undefined => state.config.metadata?.igpL0Url

export const getIsL1orL2 = (state: State.GlobalState): boolean => state.config.igpLevel === 1 || state.config.igpLevel === 2
export const getIgpLevel = (state: State.GlobalState): Gallery.Models.Url.ValidParsedQsValue<number> => state.config.igpLevel
export const getIgpType = (state: State.GlobalState): State.IgpType => state.config.igpType

export const getFaqs = (state: State.GlobalState): Gallery.ConfigApi.Faq[] | undefined => state.config.metadata?.frequentlyAskedQuestions

export const getQuantitySelector = (state: State.GlobalState): Gallery.Models.Url.ValidParsedQsValue<number> => state.config.quantity

export const getQuantityDataSelector = (state: State.GlobalState): Gallery.Models.Url.Quantity.Data => state.config.quantityData

export const getForcedRankingStrategySelector = (state: State.GlobalState): string | undefined => state.config.forcedRankingStrategy

export const selectedOptionsSelector = (state: State.GlobalState): Util.StringDictionary<string | number> => state.config.selectedOptions

export const getTenant = (state: State.GlobalState): string => state.config.tenant

export const getProductName = (state: State.GlobalState): string => state.config.metadata?.productName

export const stringRenderPropertySelector = createSelector(
  (state: State.GlobalState) => state.config.renderProperties,
  (renderProperties) =>
    (renderPropertyName: RenderProperty): State.RenderPropertyValue =>
      renderProperties[renderPropertyName],
)

export const numberRenderPropertySelector = createSelector(
  (state: State.GlobalState, props: { renderPropertyName: RenderProperty | null | undefined }): State.RenderPropertyValue =>
    props.renderPropertyName ? state.config.renderProperties[props.renderPropertyName] : null,
  (value: State.RenderPropertyValue) => (value ? parseInt(value as string, 10) : null),
)

export const getUrlPathSelector = (state: State.GlobalState): URL | string => state.config.url

export const getViewId = (state: State.GlobalState): string => state.config.viewId

export const getDesignEngagementViewIdsSelector = (state: State.GlobalState): string[] => state.config.designEngagementViewIds

export const getLatsLoadMoreLength = (state: State.GlobalState): number => state.config.lastLoadMoreLength

export const getUploadTileAvailabilitySelector = (state: State.GlobalState): boolean => state.config.uploadTileAvailable

export const getDpcId = (state: State.GlobalState): Gallery.Models.Url.ValidParsedQsValue<string> => state.config.dpcId

export const getDesignConceptId = (state: State.GlobalState): Gallery.Models.Url.ValidParsedQsValue<string> => state.config.designConceptId

export const getExperimentsSelector = (state: State.GlobalState): Gallery.ConfigApi.ExperimentData[] => state.config.experiments

export const getCategoryFilters = (state: State.GlobalState): Gallery.ConfigApi.CategoryFilter[] => state.config.categoryFilters

export const getGalleryType = (state: State.GlobalState): State.GalleryType => state.config.galleryType
